import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import {
    Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered,
    Table as TableIcon, Rows, Columns, Trash2, Undo2, Redo2, Braces, ChevronDown, SeparatorHorizontal, Eraser, FoldVertical, UnfoldVertical,
} from "lucide-react";
import { PAGE_BREAK_TOKEN } from "@/lib/sanitize";

// Lets a paragraph carry an extra bottom gap (persisted as data-space + inline margin so it
// survives save/export). Used by the "Space out paragraphs" toolbar control.
const SPACE_LEVELS = [null, "0.9em", "1.6em", "2.4em"];
const ParagraphSpacing = Extension.create({
    name: "paragraphSpacing",
    addGlobalAttributes() {
        return [{
            types: ["paragraph"],
            attributes: {
                dataSpace: {
                    default: null,
                    parseHTML: (el) => el.getAttribute("data-space"),
                    renderHTML: (attrs) => (attrs.dataSpace ? { "data-space": attrs.dataSpace, style: `margin-bottom:${attrs.dataSpace}` } : {}),
                },
            },
        }];
    },
});

const Btn = ({ onClick, active, disabled, title, children, testid }) => (
    <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled}
        className={`p-1.5 rounded-sm text-sm transition-colors ${active ? "bg-brand text-white" : "text-foreground/70 hover:bg-slate-100"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}>
        {children}
    </button>
);
const Sep = () => <span className="w-px h-5 bg-border mx-1" />;

export const RichTextEditor = ({ value, onChange, fields = [], editable = true }) => {
    const [fieldsOpen, setFieldsOpen] = React.useState(false);
    const editor = useEditor({
        editable,
        extensions: [
            StarterKit,
            Underline,
            ParagraphSpacing,
            Table.configure({ resizable: true }),
            TableRow, TableHeader, TableCell,
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
        editorProps: { attributes: { class: "rte-content", "data-testid": "rte-content" } },
    });

    // Keep external value in sync when it changes from outside (e.g. revert).
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => () => editor?.destroy(), [editor]);
    if (!editor) return null;

    const insertField = (f) => {
        editor.chain().focus().insertContent(`{{${f}}}`).run();
        setFieldsOpen(false);
    };

    // Remove empty/blank top-level paragraphs (the invisible gaps users can't see to delete). Undo-able.
    const removeBlankLines = () => {
        const doc = editor.state.doc;
        const ranges = [];
        doc.forEach((node, offset) => {
            if (node.type.name === "paragraph" && node.textContent.trim() === "") {
                ranges.push([offset, offset + node.nodeSize]);
            }
        });
        // keep at least one block so the document is never empty
        if (ranges.length >= doc.childCount && ranges.length) ranges.shift();
        if (!ranges.length) return;
        let tr = editor.state.tr;
        ranges.sort((a, b) => b[0] - a[0]).forEach(([from, to]) => { tr = tr.delete(from, to); });
        editor.view.dispatch(tr);
        editor.commands.focus();
        onChange && onChange(editor.getHTML());
    };

    // Collapse the selected paragraphs into a single tight block (joined by line breaks, no paragraph gaps).
    const tightenLines = () => {
        const { state } = editor;
        const { $from, $to } = state.selection;
        const paragraph = state.schema.nodes.paragraph;
        const hardBreak = state.schema.nodes.hardBreak;
        if (!paragraph || !hardBreak) return;
        // Expand to whole top-level blocks covered by the selection.
        const d1from = $from.depth >= 1 ? $from.before(1) : 0;
        const d1to = $to.depth >= 1 ? $to.after(1) : state.doc.content.size;
        const paras = [];
        let ok = true;
        state.doc.nodesBetween(d1from, d1to, (node, pos, parent) => {
            if (parent === state.doc) {
                if (node.type === paragraph) paras.push(node);
                else ok = false; // selection crosses a non-paragraph block (list/table/heading) — don't touch
            }
            return false; // only inspect top-level nodes
        });
        if (!ok || paras.length < 2) return;
        const content = [];
        paras.forEach((p, i) => {
            if (i > 0) content.push(hardBreak.create());
            p.content.forEach((n) => content.push(n));
        });
        const merged = paragraph.create(null, content);
        const tr = state.tr.replaceRangeWith(d1from, d1to, merged);
        editor.view.dispatch(tr);
        editor.commands.focus();
        onChange && onChange(editor.getHTML());
    };

    // Cycle a bigger gap after the selected paragraph(s) — the opposite of "Tighten lines".
    const spaceParagraphs = () => {
        const cur = editor.getAttributes("paragraph").dataSpace || null;
        const next = SPACE_LEVELS[(SPACE_LEVELS.indexOf(cur) + 1) % SPACE_LEVELS.length];
        editor.chain().focus().updateAttributes("paragraph", { dataSpace: next }).run();
    };

    return (
        <div className="border border-border rounded-sm bg-white" data-testid="rich-text-editor">
            {editable && (
                <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5 sticky top-0 bg-white z-10">
                    <Btn testid="rte-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></Btn>
                    <Btn testid="rte-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></Btn>
                    <Btn testid="rte-underline" title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></Btn>
                    <Sep />
                    <Btn testid="rte-h2" title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></Btn>
                    <Btn testid="rte-h3" title="Sub-heading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></Btn>
                    <Btn testid="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></Btn>
                    <Btn testid="rte-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Btn>
                    <Sep />
                    <Btn testid="rte-table" title="Insert table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon className="w-4 h-4" /></Btn>
                    <Btn testid="rte-add-row" title="Add row" disabled={!editor.can().addRowAfter()} onClick={() => editor.chain().focus().addRowAfter().run()}><Rows className="w-4 h-4" /></Btn>
                    <Btn testid="rte-add-col" title="Add column" disabled={!editor.can().addColumnAfter()} onClick={() => editor.chain().focus().addColumnAfter().run()}><Columns className="w-4 h-4" /></Btn>
                    <Btn testid="rte-del-row" title="Delete row" disabled={!editor.can().deleteRow()} onClick={() => editor.chain().focus().deleteRow().run()}><Trash2 className="w-4 h-4" /></Btn>
                    <Btn testid="rte-del-col" title="Delete column" disabled={!editor.can().deleteColumn()} onClick={() => editor.chain().focus().deleteColumn().run()}><Columns className="w-4 h-4 text-red-500" /></Btn>
                    <Sep />
                    {fields.length > 0 && (
                        <div className="relative">
                            <button type="button" data-testid="rte-insert-field" onMouseDown={(e) => e.preventDefault()} onClick={() => setFieldsOpen((o) => !o)}
                                className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-brand hover:bg-brand/5 border border-brand/30">
                                <Braces className="w-3.5 h-3.5" /> Insert field <ChevronDown className="w-3 h-3" />
                            </button>
                            {fieldsOpen && (
                                <div className="absolute left-0 top-full mt-1 w-72 max-h-72 overflow-auto bg-white border border-border rounded-sm shadow-lg z-20 py-1" data-testid="rte-field-menu">
                                    {fields.map((f) => (
                                        <button key={f} type="button" data-testid={`rte-field-${f}`} onMouseDown={(e) => e.preventDefault()} onClick={() => insertField(f)}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 font-mono text-foreground/80">{`{{${f}}}`}</button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <Sep />
                    <Btn testid="rte-pagebreak" title="Insert page break (forces a new page in the exported PDF)" onClick={() => editor.chain().focus().insertContent(`<p>${PAGE_BREAK_TOKEN}</p>`).run()}><SeparatorHorizontal className="w-4 h-4" /></Btn>
                    <Btn testid="rte-remove-blank" title="Remove blank lines (deletes the empty paragraphs that create vertical gaps)" onClick={removeBlankLines}><Eraser className="w-4 h-4" /></Btn>
                    <Btn testid="rte-tighten" title="Tighten lines — select two or more lines (e.g. a signature block) to pull them together with no gaps" onClick={tightenLines}><FoldVertical className="w-4 h-4" /></Btn>
                    <Btn testid="rte-space" title="Space out paragraphs — adds a bigger gap after the selected paragraph(s) to neaten page breaks; tap again for more, cycles back to normal" onClick={spaceParagraphs}><UnfoldVertical className="w-4 h-4" /></Btn>
                    <Btn testid="rte-undo" title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></Btn>
                    <Btn testid="rte-redo" title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></Btn>
                </div>
            )}
            <EditorContent editor={editor} className="rte-scroll" />
        </div>
    );
};

export default RichTextEditor;
